import { firstValueFrom, lastValueFrom, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ConfirmRegistration } from '@cl/auth/confirm-registration/confirm-registration.model';
import { Router } from '@angular/router';
import { LoginModel } from '@cl/auth/login/login.model';
import { DepartmentPermissions } from '@cl/shared/models/departments/department-permissions.model';
import { WorkTimesService } from './work-times.service';
import { ThemeService } from './theme.service';
import { CommandResult, UIThemeType } from '@cl/shared/models';
import { UserPermissions } from '@cl/shared/models/user-permissions.model';
import { ValidateOtpRequest } from '@cl/shared/models/validate-otp-request.model';
import { AuthResult } from '@cl/auth/login/auth-result.model';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public get decodedToken(): any {
        if (!this.decodedTokenCache) {
            this.decodedTokenCache = this.jwtHelper.decodeToken(this.token);
        }
        return this.decodedTokenCache;
    }

    public get userDepartmentPermissions(): DepartmentPermissions[] {
        return this.userDepartmentPermissionsCache;
    }

    public get userDepartmentPermissionsMap(): { [key: number]: DepartmentPermissions } {
        return this.userDepartmentPermissionsMapCache;
    }

    public get token(): string {
        return this.getCookieValue('auth-token');
    }

    private decodedTokenCache;
    private groupPermissionsCache: string[];
    private userDepartmentPermissionsCache: DepartmentPermissions[];
    private userDepartmentPermissionsMapCache: { [key: number]: DepartmentPermissions } = {};
    private justLoggedIn: boolean = false;

    constructor(
        private http: HttpClient,
        private jwtHelper: JwtHelperService,
        private router: Router,
        private workTimesService: WorkTimesService,
        private themeService: ThemeService,
    ) {}

    public getUserId() {
        return this.decodedToken.nameid;
    }

    public getPreferedUiThemeId(): number {
        return Number(this.decodedToken.preferedUiThemeId);
    }

    public get getUserDepartmentId(): number {
        return Number(this.decodedToken.userDepartmentId);
    }

    public get getUserMainCompanyId(): number {
        return Number(this.decodedToken.userMainCompanyId);
    }

    public get isPerformerBoss(): boolean {
        return this.decodedToken.performerBoss != null;
    }

    public get primaryPerformerId(): number {
        return Number(this.decodedToken.primaryPerformerId);
    }

    public get userPerformerIds(): number[] {
        if (!this.decodedToken.performerIds) {
            return null;
        }

        if (!Array.isArray(this.decodedToken.performerIds)) {
            return new Array(this.decodedToken.performerIds).map((id: string): number => parseInt(id, 10));
        }

        return this.decodedToken.performerIds.map((id: string): number => parseInt(id, 10));
    }

    public get totpIgnoresLeft(): number {
        if (!this.decodedToken || !this.decodedToken.totp2faIgnoresLeft) {
            return 0;
        }

        return Number(this.decodedToken.totp2faIgnoresLeft);
    }

    public get totpEnabled(): boolean {
        if (!this.decodedToken || !this.decodedToken.totpEnabled) {
            return false;
        }

        return JSON.parse(this.decodedToken.totpEnabled);
    }

    public useHasRole(roles: string[]): boolean {
        if (!this.decodedToken) {
            return false;
        }
        const userRoles = this.decodedToken.roles || [];
        return userRoles.some((role) => roles.includes(role));
    }

    public userHasPermission(permissions: string[]): boolean {
        const userPermissions: string[] = this.groupPermissionsCache || [];
        return permissions.every((permission: string): boolean => userPermissions.includes(permission));
    }

    public userHasAnyPermission(permissions: string[]): boolean {
        const userPermissions: string[] = this.groupPermissionsCache || [];
        return userPermissions.some((permission: string): boolean => permissions.includes(permission));
    }

    public userHasDepartmentPermission(departmentId: number, permissions: string[]): boolean {
        if (!this.userDepartmentPermissions || this.userDepartmentPermissions.length == 0) {
            return false;
        }

        const departmentPermissions = this.userDepartmentPermissionsMap[departmentId];
        if (!departmentPermissions) {
            return false;
        }

        return permissions.every((permission) => departmentPermissions[permission] == true);
    }

    public userHasAnyDepartmentPermission(departmentId: number, permissions: string[]): boolean {
        if (!this.userDepartmentPermissions || this.userDepartmentPermissions.length == 0) {
            return false;
        }

        const departmentPermissions = this.userDepartmentPermissionsMap[departmentId];
        if (!departmentPermissions) {
            return false;
        }

        return permissions.some((permission) => departmentPermissions[permission] == true);
    }

    public userHasPermissionInAnyDepartment(departmentIds: number[], permission: string): boolean {
        if (!this.userDepartmentPermissions || this.userDepartmentPermissions.length == 0) {
            return false;
        }

        return departmentIds.some((departmentId: number): boolean => {
            const departmentPermissions = this.userDepartmentPermissionsMap[departmentId];
            if (!departmentPermissions) {
                return false;
            }

            return departmentPermissions && departmentPermissions[permission] === true;
        });
    }

    public get loggedIn(): boolean {
        if (!this.token) {
            return false;
        }
        try {
            return !this.jwtHelper.isTokenExpired(this.token);
        } catch (ex) {
            return false;
        }
    }

    callRefreshToken(): Observable<any> {
        return this.http
            .post<UserPermissions>(`/api/v1/auth/refresh-token`, {
                currentToken: this.token,
            })
            .pipe(
                map((permissions: UserPermissions): string => {
                    this.clearCaches();
                    this.setPermissions(permissions);
                    return this.token;
                }),
                catchError((e): Observable<never> => {
                    localStorage.removeItem(`estimate-draft:${this.getUserId()}`);
                    this.router.navigate(['auth/login']);
                    return throwError(() => e);
                }),
            );
    }

    async login(loginInfo: LoginModel): Promise<AuthResult> {
        const result: AuthResult = await firstValueFrom(this.http.post<AuthResult>('/api/v1/auth/login', loginInfo));

        if (result.is2FAWithTOTPRequired) {
            return result;
        }

        this.loginSuccess(result);

        return result;
    }

    async loginWithTOTP(loginInf: LoginModel): Promise<AuthResult> {
        const result: AuthResult = await firstValueFrom(this.http.post<AuthResult>('/api/v1/auth/login/totp', loginInf));

        this.loginSuccess(result);
        return result;
    }

    // used by APP_INITIALIZER to warm the cache on full page loads
    async loadPermissions(): Promise<void> {
        if (!this.loggedIn) {
            return;
        }

        const result: CommandResult<UserPermissions> = await this.getUserPermissions();
        this.setPermissions(result.data);
    }

    async getUserPermissions(): Promise<CommandResult<UserPermissions>> {
        return await lastValueFrom(this.http.get<CommandResult<UserPermissions>>('/api/v1/auth/permissions'));
    }

    async requestPassChange(email: string): Promise<void> {
        return await firstValueFrom(this.http.post<void>('/api/v1/auth/request-pass', { email }));
    }

    async changePassword(data: ConfirmRegistration): Promise<void> {
        return await firstValueFrom(this.http.post<void>('/api/v1/auth/change-pass', data));
    }

    async confirmRegistration(data: ConfirmRegistration): Promise<void> {
        return await firstValueFrom(this.http.post<void>('/api/v1/auth/confirm-registration', data));
    }

    async logout(): Promise<void> {
        // backend deletes the auth-token cookie in the sign-out response
        await firstValueFrom(this.http.delete('/api/v1/auth/sign-out'));
        localStorage.removeItem(`estimate-draft:${this.getUserId()}`);
        this.clearCaches();
    }

    async getTOTPSetupQrCode(): Promise<CommandResult<string>> {
        return await firstValueFrom(this.http.get<CommandResult<string>>(`/api/v1/auth/2fa/otp/get-totp-setup-qr`));
    }

    async setupTOTP(data: ValidateOtpRequest): Promise<CommandResult<void>> {
        return await firstValueFrom(this.http.post<CommandResult<void>>('/api/v1/auth/2fa/otp/setup-totp', data));
    }

    removeTokenAndRedirect(): void {
        localStorage.removeItem(`estimate-draft:${this.getUserId()}`);
        this.clearCaches();
        this.router.navigate(['auth/login'], { queryParams: { returnUrl: this.router.url } });
    }

    // consumes the flag so it only reports true once, right after login
    consumeJustLoggedIn(): boolean {
        const wasJustLoggedIn: boolean = this.justLoggedIn;
        this.justLoggedIn = false;
        return wasJustLoggedIn;
    }

    private setPermissions(userPermissions: UserPermissions): void {
        this.groupPermissionsCache = userPermissions?.groupPermissions || [];
        this.userDepartmentPermissionsCache = userPermissions?.departmentPermissions || [];
        this.userDepartmentPermissionsMapCache = {};
        this.userDepartmentPermissionsCache.forEach((dp: DepartmentPermissions): void => {
            this.userDepartmentPermissionsMapCache[dp.departmentId] = dp;
        });
    }

    private clearCaches(): void {
        this.decodedTokenCache = null;
        this.groupPermissionsCache = null;
        this.userDepartmentPermissionsCache = null;
        this.userDepartmentPermissionsMapCache = null;
    }

    private getCookieValue(name: string): string {
        const nameEQ: string = name + '=';
        const ca: string[] = document.cookie.split(';');
        for (let i: number = 0; i < ca.length; i++) {
            const c: string = ca[i].trim();
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length);
        }

        return null;
    }

    private loginSuccess(authResult: AuthResult): void {
        this.justLoggedIn = true;
        this.clearCaches();
        this.workTimesService.setActiveWorkTime(null);
        localStorage.removeItem(`estimate-draft:${this.getUserId()}`);

        this.setPermissions(authResult.userPermissions);

        const preferedUiThemeId: number = this.getPreferedUiThemeId();
        this.themeService.switchTheme(preferedUiThemeId ? preferedUiThemeId : UIThemeType.Default);
    }
}
